import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { FileText, Plus, Search } from 'lucide-react'

import type { ContractDeliveryOrderStatus } from '@/lib/api/contract-delivery-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { ContractDeliveryStatusBadge } from '@/components/contract-delivery/contract-delivery-status-badge'
import { useContractDeliveries } from '@/hooks/use-contract-delivery-query'
import { useDebounced } from '@/hooks/use-debounced'
import { useAuth } from '@/hooks/use-auth'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/contract-deliveries/',
)({
    component: ContractDeliveriesPage,
})

const STATUS_OPTIONS: {
    value: ContractDeliveryOrderStatus
    label: () => string
}[] = [
    { value: 'new', label: m.cdo_status_new },
    { value: 'confirmed', label: m.cdo_status_confirmed },
    { value: 'ready_to_deliver', label: m.cdo_status_ready },
    { value: 'in_delivery', label: m.cdo_status_in_delivery },
    { value: 'delivered', label: m.cdo_status_delivered },
    { value: 'delivery_failed', label: m.cdo_status_failed },
    { value: 'cancelled', label: m.cdo_status_cancelled },
]

function ContractDeliveriesPage() {
    const { hasPermission } = useAuth()
    const [statusFilter, setStatusFilter] = useState<
        'all' | ContractDeliveryOrderStatus
    >('all')
    const [searchInput, setSearchInput] = useState('')
    const [page, setPage] = useState(1)
    const search = useDebounced(searchInput, 300)

    const { data, isLoading } = useContractDeliveries({
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: search || undefined,
        page,
        per_page: 50,
    })
    const orders = data?.data ?? []
    const meta = data?.meta

    return (
        <div className="space-y-4 p-4 md:p-6">
            <ListPageHeader
                icon={<FileText className="size-5 shrink-0" />}
                title={m.cdo_title()}
                primaryActions={
                    hasPermission('contract_delivery_orders.write') ? (
                        <Button asChild size="sm">
                            <Link
                                to="/contract-deliveries/new"
                                className="w-full"
                            >
                                <Plus className="size-4" />
                                {m.cdo_create_btn()}
                            </Link>
                        </Button>
                    ) : undefined
                }
            />

            <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[180px]">
                    <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder={m.cdo_search_ph()}
                        className="pl-9"
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value)
                            setPage(1)
                        }}
                    />
                </div>
                <Select
                    value={statusFilter}
                    onValueChange={(v) => {
                        setStatusFilter(v as 'all' | ContractDeliveryOrderStatus)
                        setPage(1)
                    }}
                >
                    <SelectTrigger className="w-full md:w-48">
                        <SelectValue placeholder={m.cdo_status_filter_ph()} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.cdo_status_filter_all()}
                        </SelectItem>
                        {STATUS_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                                {o.label()}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.cdo_col_code()}</TableHead>
                            <TableHead>{m.cdo_col_recipient()}</TableHead>
                            <TableHead>{m.cdo_col_phone()}</TableHead>
                            <TableHead>{m.cdo_col_address()}</TableHead>
                            <TableHead>{m.cdo_col_carrier()}</TableHead>
                            <TableHead>{m.cdo_col_status()}</TableHead>
                            <TableHead>{m.cdo_col_created_by()}</TableHead>
                            <TableHead>{m.cdo_col_created_at()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={8}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.cdo_loading()}
                                </TableCell>
                            </TableRow>
                        ) : orders.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={8}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.cdo_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            orders.map((o) => (
                                <TableRow key={o.id}>
                                    <TableCell>
                                        <Link
                                            to="/contract-deliveries/$id"
                                            params={{ id: o.id }}
                                            className="font-mono text-xs text-primary hover:underline"
                                        >
                                            {o.order_code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{o.recipient_name}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {o.recipient_phone}
                                    </TableCell>
                                    <TableCell className="max-w-[240px] truncate">
                                        {o.shipping_address}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {o.shipping_carrier_name || '—'}
                                    </TableCell>
                                    <TableCell>
                                        <ContractDeliveryStatusBadge
                                            status={o.status}
                                        />
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {o.created_by_name || '—'}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {new Date(
                                            o.created_at,
                                        ).toLocaleDateString('vi-VN')}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — mirror orders/index.tsx */}
            {meta && meta.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        {m.order_total_count({ count: String(meta.total) })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            {m.common_pagination_prev()}
                        </Button>
                        <span className="flex items-center px-2">
                            {page} / {meta.total_pages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            {m.common_pagination_next()}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
